import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { CheckCircle, ShieldCheck, AlertCircle, Clock, Lock, Heart, ArrowLeft } from 'lucide-react';
import CheckoutBrandHeader, { JJUMA_HOME_URL } from '../../components/public/CheckoutBrandHeader';
import { getBackendUrl } from '../../lib/env';

const API_URL = getBackendUrl();

const GATEWAY_LABELS = {
  flutterwave: 'Flutterwave',
  pesapal:     'Pesapal',
  yopayments:  'Yo! Payments',
};

const PAYMENT_METHOD_OPTIONS = [
  { label: 'MTN MoMo',   brand: 'mtn',       value: 'mobile_money', image: '/assets/images/payment-methods/mtn.png' },
  { label: 'Airtel',     brand: 'airtel',     value: 'mobile_money', image: '/assets/images/payment-methods/airtel.png' },
  { label: 'Verve',      brand: 'verve',      value: 'card' },
  { label: 'Visa',       brand: 'visa',       value: 'card',         image: '/assets/images/payment-methods/visa.png' },
  { label: 'Mastercard', brand: 'mastercard', value: 'card',         image: '/assets/images/payment-methods/mastercard.png' },
  { label: 'Amex',       brand: 'amex',       value: 'card',         image: '/assets/images/payment-methods/american.png' },
  { label: 'Google Pay', brand: 'gpay',       value: 'card',         image: '/assets/images/payment-methods/gpay.png' },
];

const GENERIC_IMG = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 48"><rect width="80" height="48" rx="8" fill="#f1f5f9"/><rect x="10" y="14" width="60" height="20" rx="4" fill="#e2e8f0"/><rect x="10" y="14" width="24" height="8" rx="3" fill="#10b981"/></svg>')}`;

function getImg(m) { return m.image || GENERIC_IMG; }
function fmtMoney(currency, value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (Number.isNaN(n)) return `${currency} ${value}`;
  return `${currency} ${n.toLocaleString()}`;
}

/* ── Compact trust row ─────────────────────────────────────── */
function TrustRow({ className = '' }) {
  return (
    <div className={`flex items-center justify-center gap-4 text-[11px] text-slate-400 ${className}`}>
      <span className="flex items-center gap-1"><Lock size={10} /> SSL Secured</span>
      <span className="flex items-center gap-1"><ShieldCheck size={10} /> Encrypted</span>
      <span className="flex items-center gap-1"><CheckCircle size={10} /> JJuma Pay</span>
    </div>
  );
}

/* ── Thank You ─────────────────────────────────────────────── */
function ThankYouPage({ success }) {
  const redirectUrl = success.redirect_url;
  const [countdown, setCountdown] = useState(redirectUrl ? 5 : null);
  const logoUrl = success.branding?.receipt_logo_url || success.branding?.logo_url || success.branding?.platform_logo_url || '';
  const isUpgrade = String(success?.purchase_type || success?.transaction?.metadata?.purchase_type || '').toLowerCase() === 'package_subscription';
  const pkgName = success?.package?.name || success?.transaction?.subscription?.package?.name || success?.transaction?.metadata?.package_name || '';

  useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);
  useEffect(() => {
    if (countdown === 0 && redirectUrl) window.location.href = redirectUrl;
  }, [countdown, redirectUrl]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-4 animate-scale-in">
        <CheckoutBrandHeader branding={success.branding} compact className="pt-2" />
        <div className="bg-white border border-slate-200 shadow-xl rounded-3xl p-7 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center shadow-sm">
            {logoUrl ? <img src={logoUrl} alt="" className="h-10 w-10 object-contain" /> : <CheckCircle size={30} className="text-emerald-600" />}
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-1.5" style={{ fontFamily: 'Outfit' }} data-testid="payment-success">
            {isUpgrade ? 'Package Activated!' : 'Payment Successful!'}
          </h1>
          <p className="text-sm text-slate-500 mb-1.5">{isUpgrade ? `Your ${pkgName || 'subscription'} is now active.` : success.thank_you_message}</p>
          {success.transaction?.receiver_name && <p className="text-xs text-slate-400 mb-4">Paid to <strong className="text-slate-700">{success.transaction.receiver_name}</strong></p>}
          <div className="bg-slate-50 rounded-2xl p-3.5 text-left space-y-2 border border-slate-100 mb-5">
            {[
              ['Amount',         fmtMoney(success.transaction?.currency, success.transaction?.amount)],
              ['Reference',      success.transaction?.reference_code],
              ['Transaction ID', success.transaction?.transaction_id],
              ['Status',         'Successful'],
              ...(isUpgrade ? [['Plan', pkgName || 'Subscription']] : []),
            ].map(([label, val]) => (
              <div key={label} className="flex items-center justify-between gap-3">
                <span className="text-xs text-slate-500">{label}</span>
                <span className="text-xs font-bold mono-nums text-slate-900 text-right break-all">{val}</span>
              </div>
            ))}
          </div>
          {redirectUrl
            ? <div><p className="text-xs text-slate-400 mb-1.5">Redirecting in <strong>{countdown}</strong>s…</p><a href={redirectUrl} className="text-emerald-700 text-sm font-bold hover:underline" data-testid="redirect-link">Continue now →</a></div>
            : <a href={JJUMA_HOME_URL} className="text-xs text-slate-400 hover:underline">Return to JJuma Pay</a>}
        </div>
        <TrustRow />
      </div>
    </div>
  );
}

/* ── Pending ───────────────────────────────────────────────── */
function PendingPaymentPage({ pending, onBack }) {
  const logoUrl = pending?.branding?.receipt_logo_url || pending?.branding?.logo_url || pending?.branding?.platform_logo_url || '';
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/20 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-4 animate-scale-in">
        <CheckoutBrandHeader branding={pending?.branding} compact className="pt-2" />
        <div className="bg-white border border-slate-200 shadow-xl rounded-3xl p-7 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center shadow-sm">
            {logoUrl ? <img src={logoUrl} alt="" className="h-10 w-10 object-contain" /> : <Clock size={30} className="text-amber-500" />}
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-1.5" style={{ fontFamily: 'Outfit' }}>Payment Request Sent</h1>
          <p className="text-sm text-slate-500 mb-4">{pending?.message || 'Check your phone to approve the Mobile Money payment.'}</p>
          {pending?.transaction && (
            <div className="bg-slate-50 rounded-2xl p-3.5 text-left space-y-2 border border-slate-100 mb-5">
              {[
                ['Amount', fmtMoney(pending.transaction.currency, pending.transaction.amount)],
                ['Reference', pending.transaction.reference_code],
                ['Transaction ID', pending.transaction.transaction_id],
                ['Status', (pending.transaction.status || 'pending').toString()],
              ].map(([label, val]) => (
                <div key={label} className="flex items-center justify-between gap-3">
                  <span className="text-xs text-slate-500">{label}</span>
                  <span className="text-xs font-bold mono-nums text-slate-900 break-all">{val}</span>
                </div>
              ))}
            </div>
          )}
          <Button type="button" variant="outline" onClick={onBack} className="border-slate-200 text-sm">
            <ArrowLeft size={14} className="mr-1.5" /> Back to checkout
          </Button>
        </div>
        <TrustRow />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN CHECKOUT PAGE
══════════════════════════════════════════════════════════════ */
export default function PaymentPage() {
  const params = useParams();
  const slug   = params.slug || params.id || '';
  const [searchParams] = useSearchParams();

  const [pageData,        setPageData]        = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState('');
  const [success,         setSuccess]         = useState(null);
  const [submitting,      setSubmitting]      = useState(false);
  const [pending,         setPending]         = useState(null);
  const [form,            setForm]            = useState({
    sender_name: '', sender_phone: '', yopayments_phone: '', sender_email: '',
    amount: '', message: '', payment_method: 'mobile_money', redirect_url: '', anonymous_donation: false,
  });
  const [selectedMethod,  setSelectedMethod]  = useState('mtn');
  const [selectedGateway, setSelectedGateway] = useState('');
  const [quote,           setQuote]           = useState(null);
  const [branding,        setBranding]        = useState({ logo_url: '', checkout_logo_url: '', receipt_logo_url: '', brand_tagline: 'Jjuma Pay' });

  useEffect(() => {
    const qpAmount   = searchParams.get('amount');
    const qpName     = searchParams.get('sender_name')  || '';
    const qpPhone    = searchParams.get('sender_phone') || '';
    const qpEmail    = searchParams.get('sender_email') || '';
    const qpMessage  = searchParams.get('message')      || '';
    const qpRedirect = searchParams.get('redirect_url') || '';
    axios.get(`${API_URL}/api/pay/${slug}`).then(r => {
      setPageData(r.data);
      const available = Array.isArray(r.data.providers_available) ? r.data.providers_available : [];
      setSelectedGateway(available[0] || '');
      const amount = qpAmount || (r.data.link?.amount_type === 'fixed' && r.data.link?.fixed_amount ? String(r.data.link.fixed_amount) : '');
      setForm(prev => ({ ...prev, amount, sender_name: qpName || prev.sender_name, sender_phone: qpPhone || prev.sender_phone, yopayments_phone: qpPhone || prev.yopayments_phone, sender_email: qpEmail || prev.sender_email, message: qpMessage || prev.message, redirect_url: qpRedirect || prev.redirect_url }));
      setLoading(false);
    }).catch(err => { setError(err.response?.data?.detail || 'Payment link not found'); setLoading(false); });
    axios.get(`${API_URL}/api/branding/public`).then(r => setBranding(r.data)).catch(() => {});
  }, [slug, searchParams]);

  useEffect(() => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { setQuote(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/pay/${slug}/quote?amount=${amt}`).then(r => setQuote(r.data)).catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(t);
  }, [form.amount, slug]);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const gatewayOptions = (pageData?.providers_available || []).map(id => ({ id, label: GATEWAY_LABELS[id] || id }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setPending(null);
    const fixedAmount = pageData?.link?.amount_type === 'fixed' && pageData?.link?.fixed_amount;
    const parsedAmount = parseFloat(form.amount);
    if (!fixedAmount && (!parsedAmount || parsedAmount <= 0)) { setSubmitting(false); setError('Please enter a valid amount.'); return; }
    if (selectedGateway === 'yopayments') {
      const normalizedPhone = String(form.yopayments_phone || form.sender_phone || '').replace(/\s+/g, '');
      const phoneOk  = /^256(70|77)\d{7}$/.test(normalizedPhone);
      const minAmount = Number(pageData?.yopayments_limits?.min_transaction_amount || 1000) || 1000;
      const maxAmount = Number(pageData?.yopayments_limits?.max_transaction_amount || 0) || 0;
      if (!normalizedPhone || !phoneOk) { setSubmitting(false); setError('Enter a valid Uganda mobile money number like 2567XXXXXXXX.'); return; }
      if (!fixedAmount && parsedAmount < minAmount) { setSubmitting(false); setError(`Minimum for Yo! Payments is ${pageData?.link?.currency || 'UGX'} ${minAmount.toLocaleString()}.`); return; }
      if (maxAmount > 0 && parsedAmount > maxAmount) { setSubmitting(false); setError(`Maximum for Yo! Payments is ${pageData?.link?.currency || 'UGX'} ${maxAmount.toLocaleString()}.`); return; }
    }
    try {
      const r = await axios.post(`${API_URL}/api/pay/${slug}`, {
        ...form,
        sender_phone: selectedGateway === 'yopayments' ? (form.yopayments_phone || form.sender_phone) : form.sender_phone,
        amount:       fixedAmount ? parseFloat(pageData.link.fixed_amount) : parsedAmount,
        redirect_url: form.redirect_url || pageData?.link?.redirect_url || '',
        provider:     selectedGateway || pageData?.link?.provider || 'auto',
      });
      if (r.data.status === 'redirect' && r.data.checkout_url) { window.location.href = r.data.checkout_url; return; }
      if ((r.data.status || '').toLowerCase() === 'pending' && (r.data.provider || '') === 'yopayments') { setPending(r.data); return; }
      if (['success','successful'].includes((r.data.status || '').toLowerCase())) { setSuccess(r.data); return; }
      setPending(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 503) setError(detail || 'Payments unavailable. Please contact the receiver.');
      else if (status === 502) setError(detail || 'Payment gateway temporarily unavailable. Try again shortly.');
      else setError(typeof detail === 'string' ? detail : 'Payment failed');
    } finally { setSubmitting(false); }
  };

  /* Loading */
  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="text-center">
        <CheckoutBrandHeader compact className="mb-5" />
        <div className="w-9 h-9 border-[3px] border-emerald-700 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-slate-400 mt-2.5">Loading…</p>
      </div>
    </div>
  );

  /* Error (no page data) */
  if (error && !pageData) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-4">
        <CheckoutBrandHeader compact className="pt-2" />
        <div className="bg-white border border-slate-200 shadow-lg rounded-3xl p-7 text-center">
          <AlertCircle size={40} className="mx-auto text-red-400 mb-3" />
          <h1 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Page Not Found</h1>
          <p className="text-slate-500 text-sm">{error}</p>
        </div>
      </div>
    </div>
  );

  if (success) return <ThankYouPage success={success} />;
  if (pending) return <PendingPaymentPage pending={pending} onBack={() => setPending(null)} />;

  const { link, owner } = pageData;
  const isDonation    = link.link_type === 'donation' || link.purpose_category === 'donation';
  const merchantLogo  = owner?.merchant_logo_url || owner?.profile_photo_url || '';
  const suggestedAmts = isDonation
    ? (Array.isArray(link.suggested_amounts) ? link.suggested_amounts : String(link.suggested_amounts || '').split(',').map(v => v.trim()).filter(Boolean))
    : [];
  const isFixed = link.amount_type === 'fixed' && link.fixed_amount;
  const checkoutBranding = { ...branding, checkout_logo_url: branding.logo_url || branding.checkout_logo_url || '', receipt_logo_url: branding.receipt_logo_url || branding.logo_url || '' };
  const displayAmount = quote
    ? fmtMoney(quote.currency, quote.customer_pays)
    : (isFixed ? fmtMoney(link.currency, link.fixed_amount) : (form.amount ? fmtMoney(link.currency, form.amount) : ''));

  const btnLabel = submitting
    ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Processing…</span>
    : isDonation
      ? <><Heart size={15} className="mr-1.5 inline" />{displayAmount ? `Donate ${displayAmount}` : 'Donate'}</>
      : <>{displayAmount ? `Pay ${displayAmount} Securely` : 'Pay Securely'}</>;

  return (
    <div className={`min-h-screen ${isDonation ? 'bg-gradient-to-br from-orange-50/50 via-amber-50/30 to-slate-50' : 'bg-gradient-to-br from-slate-50 via-emerald-50/20 to-slate-100'}`}>
      {/* ── Compact brand header ───────────────────────────── */}
      <div className="border-b border-slate-100 bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <div className="mx-auto max-w-4xl px-4 py-2 flex items-center justify-between gap-3">
          <CheckoutBrandHeader branding={checkoutBranding} compact inline className="py-0" />
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Lock size={11} className="text-emerald-600" />
            <span className="font-medium text-emerald-700">Secure Checkout</span>
          </div>
        </div>
      </div>

      {/* ── Two-column layout ──────────────────────────────── */}
      <div className="mx-auto w-full max-w-4xl px-4 py-5 lg:px-5 lg:py-6">
        <div className="grid gap-4 lg:grid-cols-[1fr_320px] lg:items-start">

          {/* ── LEFT: Merchant + form ──────────────────────── */}
          <div className="space-y-3">
            {/* Merchant header card */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <div className={`px-5 py-4 ${isDonation ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100' : 'bg-gradient-to-r from-slate-50 to-emerald-50/40 border-b border-slate-100'}`}>
                <div className="flex items-center gap-3">
                  <div className="shrink-0">
                    {merchantLogo
                      ? <img src={merchantLogo} alt={owner?.name || 'Merchant'} className="h-11 w-11 rounded-xl object-cover border border-slate-100 shadow-sm" data-testid="checkout-brand-logo" />
                      : <div className={`h-11 w-11 rounded-xl flex items-center justify-center text-base font-bold border shadow-sm ${isDonation ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>
                          {isDonation ? <Heart size={20} /> : (owner?.name?.charAt(0)?.toUpperCase() || 'J')}
                        </div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${isDonation ? 'text-amber-700' : 'text-emerald-700'}`}>
                        {isDonation ? '❤️ Donation' : '🔒 Secure Checkout'}
                      </p>
                      {owner?.verified && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                          <ShieldCheck size={9} /> Verified
                        </span>
                      )}
                    </div>
                    <h1 className="text-lg font-bold text-slate-900 leading-tight truncate" style={{ fontFamily: 'Outfit' }} data-testid="payment-title">{link.title}</h1>
                    {link.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{link.description}</p>}
                  </div>
                </div>
                {isDonation && link.donation_story && (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-white/60 px-3 py-2.5">
                    <p className="text-xs leading-5 text-amber-950 line-clamp-3">{link.donation_story}</p>
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="mx-4 mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 flex items-start gap-2 text-xs text-red-700" data-testid="payment-error">
                  <AlertCircle size={13} className="mt-0.5 shrink-0" /><span>{error}</span>
                </div>
              )}

              {/* Form */}
              <form id="public-checkout-form" onSubmit={handleSubmit} className="px-5 py-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">{isDonation ? 'Donor Name' : 'Your Name'}</Label>
                  <Input data-testid="pay-sender-name" value={form.sender_name} onChange={e=>upd('sender_name',e.target.value)} placeholder="Full name" className="mt-1 rounded-xl border-slate-200 h-9 text-sm" required />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Phone Number</Label>
                  <Input data-testid="pay-sender-phone" value={form.sender_phone} onChange={e=>upd('sender_phone',e.target.value)} placeholder="+256…" className="mt-1 rounded-xl border-slate-200 h-9 text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">Email Address</Label>
                  <Input data-testid="pay-sender-email" type="email" value={form.sender_email} onChange={e=>upd('sender_email',e.target.value)} placeholder="your@email.com" className="mt-1 rounded-xl border-slate-200 h-9 text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">{isDonation ? 'Message (optional)' : 'Message (optional)'}</Label>
                  <Textarea data-testid="pay-message" value={form.message} onChange={e=>upd('message',e.target.value)} placeholder={isDonation ? 'Leave a message of support…' : 'Leave a note…'} className="mt-1 rounded-xl border-slate-200 text-sm resize-none" rows={2} />
                </div>
              </form>
            </div>
          </div>

          {/* ── RIGHT: Payment Summary (sticky) ───────────── */}
          <div className="lg:sticky lg:top-[57px]">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Payment Summary</p>
              </div>

              <div className="px-4 py-3 space-y-3">
                {/* Amount */}
                <div className={`rounded-xl border px-3 py-2.5 ${isDonation ? 'border-amber-100 bg-amber-50/50' : 'border-slate-100 bg-slate-50'}`}>
                  {isFixed ? (
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Amount</p>
                      <p className="text-2xl font-bold text-slate-900 mono-nums mt-0.5" data-testid="fixed-amount-display">{fmtMoney(link.currency, link.fixed_amount)}</p>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">{isDonation ? `Donation Amount (${link.currency})` : `Amount (${link.currency})`}</Label>
                      <Input data-testid="pay-amount" type="number" value={form.amount} onChange={e=>upd('amount',e.target.value)} placeholder={isDonation ? 'Enter amount' : 'Enter amount'} className="mt-1 rounded-xl border-slate-200 text-base font-bold h-10" required />
                    </div>
                  )}
                  {quote && (
                    <div className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50 px-2.5 py-2 text-xs">
                      <div className="flex justify-between gap-2"><span className="text-emerald-800">You pay</span><strong className="mono-nums text-emerald-900">{fmtMoney(quote.currency, quote.customer_pays)}</strong></div>
                      <div className="flex justify-between gap-2 mt-0.5 text-emerald-700/80"><span>Merchant receives</span><span className="mono-nums">{fmtMoney(quote.currency, quote.merchant_receives)}</span></div>
                    </div>
                  )}
                </div>

                {/* Suggested amounts */}
                {isDonation && suggestedAmts.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-1.5">Suggested</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {suggestedAmts.map(amt => (
                        <button key={amt} type="button" onClick={() => upd('amount', String(amt))}
                          className={`rounded-xl border py-2 text-xs font-bold transition-all ${String(form.amount)===String(amt)?'border-amber-500 bg-amber-50 text-amber-800':'border-slate-200 bg-white text-slate-700 hover:border-amber-200 hover:bg-amber-50/40'}`}>
                          {fmtMoney(link.currency, amt)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Anonymous donation */}
                {isDonation && link.anonymous_allowed && (
                  <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-100 transition-colors">
                    <input type="checkbox" checked={form.anonymous_donation||false} onChange={e=>upd('anonymous_donation',e.target.checked)} className="h-3.5 w-3.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500" />
                    <span className="font-semibold">Donate anonymously</span>
                  </label>
                )}

                {/* Payment method section */}
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-1.5">Payment Method</p>

                  {/* Gateway selector */}
                  {gatewayOptions.length > 1 && (
                    <div className="mb-2 space-y-1.5">
                      <div className="flex gap-1.5">
                        {gatewayOptions.map(gw => (
                          <button key={gw.id} type="button" onClick={() => setSelectedGateway(gw.id)}
                            className={`flex-1 rounded-xl border py-1.5 text-xs font-bold transition-all ${selectedGateway===gw.id?'border-emerald-600 bg-emerald-50 text-emerald-800':'border-slate-200 bg-white text-slate-700 hover:border-emerald-200'}`}
                            data-testid={`gateway-${gw.id}`}>
                            {gw.label}
                          </button>
                        ))}
                      </div>
                      {selectedGateway === 'yopayments' && (
                        <div>
                          <Label className="text-xs font-semibold text-slate-600">Mobile Money Number</Label>
                          <Input data-testid="pay-yopayments-phone" value={form.yopayments_phone} onChange={e=>upd('yopayments_phone',e.target.value)} placeholder="25677XXXXXXX" className="mt-1 rounded-xl border-slate-200 h-9 text-sm" />
                          <p className="text-[10px] text-slate-400 mt-1">Yo! Payments will send a prompt to this number.</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment method pills — compact horizontal wrap */}
                  <div className="grid grid-cols-4 gap-1.5">
                    {PAYMENT_METHOD_OPTIONS.map(method => (
                      <button key={method.label} type="button"
                        onClick={() => { setSelectedMethod(method.brand); upd('payment_method', method.value); }}
                        aria-label={method.label}
                        title={method.label}
                        className={`relative flex h-11 items-center justify-center rounded-xl border p-1.5 transition-all duration-150 ${selectedMethod===method.brand?'border-emerald-600 bg-emerald-50 shadow-sm ring-1 ring-emerald-200':'border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/30'}`}
                        data-testid={`pay-method-${method.brand}`}>
                        <span className="sr-only">{method.label}</span>
                        <img src={getImg(method)} alt="" aria-hidden loading="lazy" className="max-h-7 w-full max-w-[56px] object-contain" />
                        {selectedMethod===method.brand && (
                          <span className="absolute -right-1 -top-1 w-4 h-4 flex items-center justify-center rounded-full bg-emerald-700 text-white shadow">
                            <CheckCircle size={9} />
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA button */}
                <Button form="public-checkout-form" type="submit" data-testid="submit-payment-btn" disabled={submitting}
                  className={`w-full h-11 rounded-xl text-sm font-bold shadow-md transition-all ${isDonation?'bg-amber-600 hover:bg-amber-700 text-white':'bg-emerald-700 hover:bg-emerald-800 text-white'}`}>
                  {btnLabel}
                </Button>

                {/* Trust footer */}
                <TrustRow className="py-0.5" />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
